/**
 * Backend Authentication API Service
 * Handles authentication with the backend API for both M365 and password login
 */

import { getApiBaseUrl } from '../config/env';
import { getToken } from '@/services/authService';
import { apiGet } from '@/shared/api/apiClient';

const API_BASE_URL = getApiBaseUrl();

/**
 * M365 Login Request DTO - matches backend DTO
 */
export interface M365LoginRequest {
  azureId: string;
  displayName: string;
  email: string;
  jobTitle?: string;
  department?: string;
  officeLocation?: string;
  mobilePhone?: string;
  businessPhones?: string[];
  profilePhoto?: string;
  subdomain: string;
  tenantId: string; // Tenant ID from identity_configs
}

/**
 * Password Login Request DTO - matches backend DTO
 */
export interface PasswordLoginRequest {
  email: string;
  password: string;
  tenantId?: string; // Optional tenant ID (defaults to 00000000-0000-0000-0000-000000000000)
}

/**
 * User Info from Backend
 */
export interface UserInfo {
  id: string;
  email: string;
  name: string;
  jobTitle: string;
  department: string;
  avatar?: string;
  photoUrl?: string;
  employeeId: string;
  tenantId: string;
  role?: string; // Added role field for better user context
  isOnboardingComplete?: boolean; // Onboarding wizard completion status
  // Probation fields
  employmentStatus?: string;
  isProbation?: boolean;
  probationStartDate?: string;
  probationEndDate?: string;
  probationDurationDays?: number;
  daysRemaining?: number;
  isProbationComplete?: boolean;
}

/**
 * Authentication Response from Backend
 */
export interface AuthResponse {
  success: boolean;
  token: string;
  user: UserInfo;
  isNewUser: boolean;
  requiresPasswordChange?: boolean;
  error?: string;
}

/**
 * Profile returned by GET /auth/me — the authoritative copy of the fields the
 * access token does not carry (employee code, department, job title).
 */
export interface CurrentUserInfo {
  id: string;
  email: string;
  name: string;
  jobTitle: string;
  department: string;
  role: string;
  employeeId: string;
  tenantId: string;
}

/**
 * Fetch the signed-in user's profile from the token already on the request.
 *
 * Used when a reopened tab has an authenticated session but no cached profile:
 * the JWT alone yields placeholder department/employee code.
 */
export async function getCurrentUser(): Promise<CurrentUserInfo> {
  return apiGet<CurrentUserInfo>('/auth/me');
}

/**
 * Authenticate user with M365 credentials via backend
 * @param request M365 login request data
 * @returns Authentication response with JWT token
 */
export async function authenticateWithM365(request: M365LoginRequest): Promise<AuthResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/m365-login`, {
      method: 'POST',
      credentials: 'include', // honor the Set-Cookie refresh token on this response
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Authentication failed' }));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data: AuthResponse = await response.json();
    return data;
  } catch (error) {
    console.error('M365 authentication error:', error);
    throw error;
  }
}

/**
 * Authenticate user with email and password via backend
 * @param request Password login request data
 * @returns Authentication response with JWT token
 */
export async function authenticateWithPassword(request: PasswordLoginRequest): Promise<AuthResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      credentials: 'include', // honor the Set-Cookie refresh token on this response
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Invalid email or password' }));
      throw new Error(errorData.error || 'Invalid email or password');
    }

    const data: AuthResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Password authentication error:', error);
    throw error;
  }
}

/**
 * Request a password reset. Backend generates a temporary password and emails it.
 * Always resolves (never throws on "email not found") to prevent user enumeration.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || 'Failed to send reset email. Please try again.');
  }
}

/**
 * Set a new permanent password after logging in with a temporary one.
 * Requires a valid JWT (from the temporary-password login) in the current session.
 */
export async function changePassword(newPassword: string): Promise<void> {
  const token = getToken();
  const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ newPassword }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || 'Failed to change password. Please try again.');
  }
}

/**
 * Set the initial password for an account that was created without one.
 * This endpoint is public — no token required.
 */
export async function setInitialPassword(email: string, newPassword: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/auth/set-initial-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, newPassword }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || 'Failed to set password. Please try again.');
  }
}

/**
 * Revokes the current session's refresh-token cookie server-side. Best-effort —
 * the client-side session is cleared regardless (see AuthContext.logout), so a
 * network failure here must never block logging out locally.
 */
export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE_URL}/auth/logout`, { method: 'POST', credentials: 'include' });
  } catch (error) {
    console.error('Backend logout call failed:', error);
  }
}

/**
 * Revokes every refresh-token session for the current user ("sign out
 * everywhere"). Requires a currently valid access token.
 */
export async function logoutAllSessions(accessToken: string): Promise<void> {
  try {
    await fetch(`${API_BASE_URL}/auth/logout-all`, {
      method: 'POST',
      credentials: 'include',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  } catch (error) {
    console.error('Backend logout-all call failed:', error);
  }
}
